import { FilePlusIcon } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

import { InputError } from "@/components/primitives/InputError";
import { InputErrorIndicator } from "@/components/primitives/InputErrorIndicator";
import FieldLabel from "@/components/primitives/InputLabel";
import { FormType } from "@/integrations";
import { useToast } from "@/providers/ToastProvider";
import React from "react";
import classNames from "@/helpers/classNames";
import ButtonPrimitive from "@/components/primitives/Button";
import { useDropzone } from "react-dropzone";

export default function FileUploadForm({
  field,
  loading: _loading,
  ..._rest
}: {
  loading?: boolean;
  field: Extract<FormType, { type: "file-upload" }>;
  descriptionKey?: string;
}) {
  const {
    name: id,
    required,
    label,
    validityCheckString,
    description,
    extensions,
  } = field;

  const {
    formState: { errors },
    control,
  } = useFormContext();

  const error = errors?.[id]?.message;
  return (
    <div>
      <FieldLabel id={id} required={required}>
        {label}
      </FieldLabel>

      <div className="flex items-center">
        <div className="grow">
          <Controller
            control={control}
            render={({
              field: { onChange, onBlur: _onBlur, value: _value },
            }) => {
              return (
                <Dropzone
                  validityCheckString={validityCheckString}
                  description={description}
                  extensions={extensions}
                  onChange={onChange}
                />
              );
            }}
            rules={{ required }}
            name={id}
          />
        </div>

        {!!error && <InputErrorIndicator className="ml-2" />}
      </div>
      {!!error && <InputError className="mt-1">{String(error)}</InputError>}
    </div>
  );
}

type DropzoneProps = {
  onChange: (privateKey: string) => void;
  validityCheckString?: string;
  description?: string;
  extensions?: string[];
};

const Dropzone = ({
  onChange,
  validityCheckString,
  description,
  extensions,
}: DropzoneProps) => {
  const toast = useToast();
  const [filename, setFilename] = React.useState<string | null>(null);

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        // eslint-disable-next-line no-console
        reader.onabort = () => console.warn("file reading was aborted");
        // eslint-disable-next-line no-console
        reader.onerror = () => console.warn("file reading has failed");
        reader.onload = () => {
          const result = reader.result?.toString();
          if (
            result &&
            (!validityCheckString ||
              (validityCheckString && result.includes(validityCheckString)))
          ) {
            onChange(result);
            setFilename(file.name);
          } else {
            setFilename(null);
            toast("Invalid file", "The provided file is not valid.", "error");
          }
        };
        reader.readAsText(file, "UTF-8");
      });
    },
    [onChange, toast, validityCheckString],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: extensions?.length
      ? {
          "": extensions,
        }
      : undefined,
    useFsAccessApi: false,
  });

  return (
    <div
      className={"mt-1 sm:col-span-2 sm:mt-0"}
      {...getRootProps()}
      role="button"
    >
      <input {...getInputProps()} />
      {!filename ? (
        <div
          className={classNames(
            "flex justify-center px-6 pt-5 pb-6",
            "rounded-md border-2 border-dashed",
            "hover:border-blue-200 dark:hover:border-blue-500",
            "hover:bg-blue-50 dark:hover:bg-blue-600/20",
            isDragActive &&
              "border-blue-200 bg-blue-50 dark:border-blue-500 dark:bg-blue-600/20",
          )}
        >
          <div className="space-y-1 text-center">
            <FilePlusIcon className="mx-auto size-12 text-gray-400" />
            <div className="text-sm text-gray-600">
              <span
                className={classNames(
                  "font-medium text-blue-500",
                  "focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 focus-within:outline-hidden",
                )}
              >
                <span>{description}</span>
              </span>
              <p className="pl-1">or drag and drop</p>
            </div>
            {extensions && (
              <p className="text-xs text-gray-500">{extensions.join(", ")}</p>
            )}
          </div>
        </div>
      ) : (
        <ButtonPrimitive
          onClick={(e) => e.preventDefault()}
          className="flex h-12 w-full items-center rounded-sm border bg-background px-4 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <span className="truncate">{filename}</span>
        </ButtonPrimitive>
      )}
    </div>
  );
};
